/**
 * Invitation code generator.
 *
 * Produces short, human-friendly join codes from an unambiguous alphabet
 * (no 0/O, 1/I/L) using the Web Crypto API. Matches the server-side format
 * guard in `create_family_invitation` (^[A-Z0-9]{6,12}$).
 *
 * @module features/family/lib/inviteCode
 */

/** Unambiguous uppercase alphabet (no 0, O, 1, I, L). */
const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

export const INVITE_CODE_LENGTH = 8;

/** Server/client shared validation shape for invitation codes. */
export const INVITE_CODE_PATTERN = /^[A-Z0-9]{6,12}$/;

/**
 * Generate a cryptographically random invitation code.
 *
 * @param length - Code length (default {@link INVITE_CODE_LENGTH}).
 */
export function generateInviteCode(
  length: number = INVITE_CODE_LENGTH
): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let code = '';
  for (const byte of bytes) {
    code += CODE_ALPHABET[byte % CODE_ALPHABET.length];
  }
  return code;
}

/**
 * Normalize user input into canonical code form (uppercase, trimmed).
 */
export function normalizeInviteCode(input: string): string {
  return input.trim().toUpperCase();
}

/**
 * Whether the given string is a structurally valid invitation code.
 */
export function isValidInviteCode(input: string): boolean {
  return INVITE_CODE_PATTERN.test(normalizeInviteCode(input));
}

/**
 * Build the shareable onboarding deep link for an invitation code.
 * The link ALWAYS carries the family id (`/onboarding?code=…&family=…`) so
 * the invited account can be connected to the specific family.
 */
export function buildInviteLink(code: string, familyId: string): string {
  return `${window.location.origin}/onboarding?code=${code}&family=${familyId}`;
}
